import { all, AllEffect, ForkEffect, takeEvery } from 'redux-saga/effects';

/* Sagas */
import { getSharedSnapSaga } from './SharedSnap/sagas';
import {
	autoLoginSaga,
	getAuthLinkSaga,
	loginSaga,
	logOutSaga,
	socialSaga,
	startSyncSaga,
} from './Auth/sagas';
import { getUserSaga } from './User/sagas';
import { getMyTeamSaga } from './MyTeam/sagas';
import { addUserToTeamSaga, getTeamsSaga } from './Teams/sagas';
import { addCommentSaga, getCommentsSaga } from './Comments/sagas';
import {
	exportChartSaga,
	getChartsSaga,
	inviteMembersSaga,
	shareByLinkChartSaga,
} from './Charts/sagas';
import {
	addToSnapshotsSaga,
	getSnapshotsSaga,
	loadMoreSnapshotsSaga,
	removeFromSnapshotsSaga,
} from './Snapshots/sagas';
import {
	addFavoriteSnapSaga,
	getFavoritesSaga,
	loadMoreFavoritesSaga,
	removeFavoriteSnapSaga,
} from './Favorites/sagas';
import {
	addSnapSaga,
	deleteSnapSaga,
	editSnapSaga,
	exportSnapSaga,
	getSnapsSaga,
} from './Snaps/sagas';
import {
	connectGongSaga,
	connectIntercomSaga,
	connectOutreachSaga,
	connectZendeskSaga,
	disconnectApplicationSaga,
	getApplicationsSaga,
	sendCodeOutreachSaga,
} from './Applications/sagas';
import {
	deleteSharedTeamRoomSaga,
	getSharedTeamRoomsSaga,
	loadMoreSharedTeamRoomsSaga,
} from './SharedTeamRooms/sagas';
import {
	deleteMineTeamRoomSaga,
	getMineTeamRoomsSaga,
	loadMoreMineTeamRoomsSaga,
	shareSnapSaga,
} from './MineTeamRooms/sagas';
import { getMembersSaga, inviteMemberSaga } from './Members/sagas';
import {
	approveInviteSaga,
	getInvitesSaga,
	sendInviteSaga,
} from './Invites/sagas';

/* TYPES */
import { GET_SHARED_SNAP } from './SharedSnap/types';
import {
	AUTO_LOGIN,
	GET_AUTH_LINK,
	LOGIN,
	LOGOUT,
	SOCIAL,
	START_SYNC,
} from './Auth/types';
import { GET_USER } from './User/types';
import { GET_MY_TEAM } from './MyTeam/types';
import { ADD_USER_TO_TEAM, GET_TEAMS } from './Teams/types';
import { ADD_COMMENT, GET_COMMENTS } from './Comments/types';
import {
	EXPORT_CHART,
	GET_CHARTS,
	INVITE_MEMBERS,
	SHARE_CHART,
} from './Charts/types';
import {
	ADD_TO_SNAPSHOTS,
	DELETE_SNAPSHOT,
	GET_SNAPSHOTS,
	LOAD_MORE_SNAPSHOTS,
} from './Snapshots/types';
import {
	ADD_FAVORITE_SNAP,
	DELETE_FAVORITE_SNAP,
	GET_FAVORITES,
	LOAD_MORE_FAVORITES,
} from './Favorites/types';
import {
	ADD_SNAP,
	DELETE_SNAP,
	EDIT_SNAP,
	EXPORT_SNAP,
	GET_SNAPS,
} from './Snaps/types';
import {
	CONNECT_OUTREACH,
	CONNECT_INTERCOM,
	DISCONNECT_APP,
	GET_APPLICATIONS,
	SEND_CODE_OUTREACH,
	CONNECT_GONG,
	CONNECT_ZENDESK,
} from './Applications/types';
import {
	DELETE_MY_TEAM_ROOM,
	GET_MY_TEAM_ROOMS,
	LOAD_MORE_MY_TEAM_ROOMS,
	SHARE_SNAP,
} from './MineTeamRooms/types';
import {
	DELETE_SHARED_TEAM_ROOM,
	GET_SHARED_TEAM_ROOMS,
	LOAD_MORE_SHARED_TEAM_ROOMS,
} from './SharedTeamRooms/types';
import { GET_MEMBERS, INVITE_MEMBER } from './Members/types';
import { APPROVE_INVITE, GET_INVITES, SEND_INVITE } from './Invites/types';

export function* watchAll(): Generator<
	AllEffect<ForkEffect<never>>,
	void,
	unknown
> {
	yield all([
		takeEvery(GET_SHARED_SNAP, getSharedSnapSaga),
		takeEvery(LOGIN, loginSaga),
		takeEvery(SOCIAL, socialSaga),
		takeEvery(AUTO_LOGIN, autoLoginSaga),
		takeEvery(GET_AUTH_LINK, getAuthLinkSaga),
		takeEvery(LOGOUT, logOutSaga),
		takeEvery(GET_USER, getUserSaga),
		takeEvery(GET_MY_TEAM, getMyTeamSaga),
		takeEvery(GET_TEAMS, getTeamsSaga),
		takeEvery(ADD_USER_TO_TEAM, addUserToTeamSaga),
		takeEvery(START_SYNC, startSyncSaga),
		takeEvery(ADD_COMMENT, addCommentSaga),
		takeEvery(GET_CHARTS, getChartsSaga),
		takeEvery(GET_SNAPSHOTS, getSnapshotsSaga),
		takeEvery(LOAD_MORE_SNAPSHOTS, loadMoreSnapshotsSaga),
		takeEvery(GET_FAVORITES, getFavoritesSaga),
		takeEvery(LOAD_MORE_FAVORITES, loadMoreFavoritesSaga),
		takeEvery(INVITE_MEMBERS, inviteMembersSaga),
		takeEvery(SHARE_CHART, shareByLinkChartSaga),
		takeEvery(EXPORT_CHART, exportChartSaga),
		takeEvery(EXPORT_SNAP, exportSnapSaga),
		takeEvery(DELETE_SNAPSHOT, removeFromSnapshotsSaga),
		takeEvery(DELETE_FAVORITE_SNAP, removeFavoriteSnapSaga),
		takeEvery(GET_APPLICATIONS, getApplicationsSaga),
		takeEvery(CONNECT_OUTREACH, connectOutreachSaga),
		takeEvery(CONNECT_INTERCOM, connectIntercomSaga),
		takeEvery(CONNECT_GONG, connectGongSaga),
		takeEvery(CONNECT_ZENDESK, connectZendeskSaga),
		takeEvery(SEND_CODE_OUTREACH, sendCodeOutreachSaga),
		takeEvery(DISCONNECT_APP, disconnectApplicationSaga),
		takeEvery(GET_MY_TEAM_ROOMS, getMineTeamRoomsSaga),
		takeEvery(LOAD_MORE_MY_TEAM_ROOMS, loadMoreMineTeamRoomsSaga),
		takeEvery(GET_SHARED_TEAM_ROOMS, getSharedTeamRoomsSaga),
		takeEvery(LOAD_MORE_SHARED_TEAM_ROOMS, loadMoreSharedTeamRoomsSaga),
		takeEvery(GET_COMMENTS, getCommentsSaga),
		takeEvery(DELETE_MY_TEAM_ROOM, deleteMineTeamRoomSaga),
		takeEvery(DELETE_SHARED_TEAM_ROOM, deleteSharedTeamRoomSaga),
		takeEvery(GET_SNAPS, getSnapsSaga),
		takeEvery(ADD_SNAP, addSnapSaga),
		takeEvery(EDIT_SNAP, editSnapSaga),
		takeEvery(DELETE_SNAP, deleteSnapSaga),
		takeEvery(ADD_TO_SNAPSHOTS, addToSnapshotsSaga),
		takeEvery(ADD_FAVORITE_SNAP, addFavoriteSnapSaga),
		takeEvery(SHARE_SNAP, shareSnapSaga),
		takeEvery(GET_MEMBERS, getMembersSaga),
		takeEvery(INVITE_MEMBER, inviteMemberSaga),
		takeEvery(GET_INVITES, getInvitesSaga),
		takeEvery(SEND_INVITE, sendInviteSaga),
		takeEvery(APPROVE_INVITE, approveInviteSaga),
	]);
}
